import { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { apiClient } from '../lib/apiClient';
import CrossIcon from './icons/cross.svg?react';
import ScrollIcon from './icons/scroll.svg?react';
import CheckIcon from './icons/checkcircle.svg?react';
import PaperClipIcon from './icons/paperclip.svg?react';
import CameraIcon from './icons/camera.svg?react';
import SaveBtn from './microComponents/SaveBtn.jsx';
import Spinner from './Spinner.jsx';
import { AttachmentChip } from './AttachmentChip.jsx';
import { ImagePreviewModal } from './ImagePreviewModal.jsx';

// File validation constants (aligned with main TextArea)
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png'];
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes
const MAX_ATTACHMENTS = 2; // Max total attachments (files + screenshot)

/* ------------------------------------------------------------------ */
/*  tiny hooks                                                         */
/* ------------------------------------------------------------------ */
function useOutsideClick(open, refs, closeFn) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (refs.every(r => r.current && !r.current.contains(e.target))) closeFn();
    };
    document.addEventListener('mousedown', handler, { passive: true });
    return () => document.removeEventListener('mousedown', handler);
  }, [open, closeFn, refs]);
}

function useEscapeToClose(open, setOpen) {
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (open) {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, setOpen]);
}

const FeedbackElement = forwardRef((props, ref) => {
  const [isActive, setIsActive] = useState(false);
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [attachments, setAttachments] = useState([]); // Array of File objects
  const [screenshot, setScreenshot] = useState(null); // Blob for screenshot
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [formState, setFormState] = useState("idle");
  const [localFeedback, setLocalFeedback] = useState(feedback); // Local for fast typing
  const [isSmallScreen, setIsSmallScreen] = useState(false); // Aligned with menu: true if <768px

  // Image preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(null);

  const popoverRef = useRef(null);
  const modalRef = useRef(null); // For small-screen modal
  const feedbackBtnRef = useRef(null);
  const feedbackTextareaRef = useRef(null);
  const fileInputRef = useRef(null); // Ref for hidden file input

  // Expose both popover and modal refs to parent
  useImperativeHandle(ref, () => ({
    popoverRef: popoverRef.current,
    modalRef: modalRef.current,
  }), [open, isSmallScreen]);

  const closeFeedback = useCallback(() => {
    setFormState("idle");
    setOpen(false);
  }, []);

  /* ---------- custom hooks ---------- */
  // Outside click for popover (large screens only)
  useOutsideClick(open && !isSmallScreen, [popoverRef, feedbackBtnRef], closeFeedback);

  useEscapeToClose(open, closeFeedback);

  // Sync localFeedback from feedback if it changes externally
  useEffect(() => {
    setLocalFeedback(feedback);
  }, [feedback]);

  const submitFeedback = useCallback(async () => {
    setFormState("loading");
    const formData = new FormData();
    formData.append('feedback', localFeedback); // Use local for submission
    attachments.forEach(file => formData.append('attachments', file));
    if (screenshot) formData.append('screenshot', screenshot, 'screenshot.png');

    try {
      // Replace with your real API endpoint
      const response = await apiClient.authenticatedRequest('/api/feedback', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error('Upload failed');

      // Simulate delay for demo (remove in production)
      await new Promise(resolve => setTimeout(resolve, 2000));

      setFormState("success");
      setFeedback(""); // Sync back to main state on success
      setLocalFeedback(""); // Clear local
      setAttachments([]);
      setScreenshot(null);

      setTimeout(() => {
        closeFeedback();
      }, 2000);
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to send feedback. Please try again.');
      setFormState("idle");
    }
  }, [localFeedback, attachments, screenshot, closeFeedback]);

  // File validation
  const validateFiles = useCallback((filesToValidate) => {
    const invalidFiles = [];
    const validFiles = [];

    filesToValidate.forEach((file) => {
      const isValidType = ALLOWED_FILE_TYPES.includes(file.type);
      const isValidSize = file.size <= MAX_FILE_SIZE;

      if (!isValidType) {
        invalidFiles.push(`${file.name}: Invalid file type. Only JPEG and PNG images are allowed.`);
      } else if (!isValidSize) {
        invalidFiles.push(`${file.name}: File too large. Maximum size is ${Math.round(MAX_FILE_SIZE / (1024 * 1024))}MB.`);
      } else {
        validFiles.push(file);
      }
    });

    return { validFiles, invalidFiles };
  }, []);

  // Handle file selection (from input) with max limit and duplicate detection
  const handleFileChange = useCallback((e) => {
    const newFiles = Array.from(e.target.files);
    const { validFiles, invalidFiles } = validateFiles(newFiles);

    // Deduplicate files based on name:size:lastModified
    const fileKey = (f) => `${f.name}:${f.size}:${f.lastModified}`;
    const existing = new Set(attachments.map(fileKey));
    const uniqueFiles = validFiles.filter(f => !existing.has(fileKey(f)));

    if (uniqueFiles.length < validFiles.length) {
      toast.error('Duplicate files were skipped.');
    }

    const currentCount = attachments.length + (screenshot ? 1 : 0);
    let addableFiles = uniqueFiles.slice(0, MAX_ATTACHMENTS - currentCount);

    if (uniqueFiles.length > addableFiles.length) {
      toast.error(`You can only add up to ${MAX_ATTACHMENTS} attachments total. Only ${addableFiles.length} were added.`);
    }

    if (invalidFiles.length > 0) {
      toast.error(`Some files could not be added:\n\n${invalidFiles.join('\n')}`);
    }

    if (addableFiles.length > 0) {
      setAttachments(prev => [...prev, ...addableFiles]);
    }
    e.target.value = ''; // Reset input
  }, [attachments, screenshot, validateFiles]);

  // Remove attachment
  const removeAttachment = useCallback((index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Preview URLs with cleanup
  const attachmentPreviews = useMemo(() => 
    attachments.map(file => ({ url: URL.createObjectURL(file), name: file.name })),
    [attachments]
  );

  const screenshotPreview = useMemo(() => 
    screenshot ? URL.createObjectURL(screenshot) : null,
    [screenshot]
  );

  // Cleanup URLs on unmount or when attachments change
  useEffect(() => {
    return () => {
      attachmentPreviews.forEach(p => URL.revokeObjectURL(p.url));
      if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    };
  }, [attachmentPreviews, screenshotPreview]);

  // Image preview handlers
  const handleImagePreviewClick = useCallback(({ src, alt }) => {
    setSelectedPreview({ src, alt });
    setShowPreviewModal(true);
  }, []);

  const handlePreviewModalClose = useCallback(() => {
    setSelectedPreview(null);
    setShowPreviewModal(false);
  }, []);

  // Capture screenshot using native Screen Capture API with max limit
  const captureScreenshot = useCallback(async () => {
    const currentCount = attachments.length + (screenshot ? 1 : 0);
    if (currentCount >= MAX_ATTACHMENTS) {
      toast.error(`You can only add up to ${MAX_ATTACHMENTS} attachments total.`);
      return;
    }

    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error('Screen capture is not supported in this browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always' },
      });

      const track = stream.getVideoTracks()[0];
      const imageCapture = new ImageCapture(track);
      const bitmap = await imageCapture.grabFrame();

      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const context = canvas.getContext('bitmaprenderer');
      if (context) {
        context.transferFromImageBitmap(bitmap);
      } else {
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
      }

      canvas.toBlob((blob) => {
        setScreenshot(blob);
        track.stop();
        stream.getTracks().forEach(t => t.stop());
      }, 'image/png');
    } catch (error) {
      console.error('Screenshot error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Screen capture permission denied.');
      } else {
        toast.error('Failed to capture screenshot. Please try again.');
      }
    }
  }, [attachments, screenshot]);

  // Remove screenshot
  const removeScreenshot = useCallback(() => {
    setScreenshot(null);
  }, []);

  // Detect small screens (exact match to menu: <768px) and handle resize
  useEffect(() => {
    const checkSmallScreen = () => {
      const newIsSmallScreen = window.innerWidth < 768;
      if (newIsSmallScreen !== isSmallScreen && open) {
        closeFeedback(); // Close on mode switch to avoid glitches
      }
      setIsSmallScreen(newIsSmallScreen);
    };

    checkSmallScreen();
    window.addEventListener('resize', checkSmallScreen);
    return () => window.removeEventListener('resize', checkSmallScreen);
  }, [isSmallScreen, open, closeFeedback]);

  // Position calculation (large screens only)
  useEffect(() => {
    if (open && !isSmallScreen && feedbackBtnRef.current) {
      const btnRect = feedbackBtnRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: btnRect.top - 256 - 8,
        left: btnRect.left,
      });
    }
  }, [open, isSmallScreen]);

  useEffect(() => {
    if (open && feedbackTextareaRef.current) {
      feedbackTextareaRef.current.focus();
    }
  }, [open]);

  // New: Fast local update for typing
  const onInputChangeEvent = useCallback((e) => {
    setLocalFeedback(e.target.value);
  }, []);

  // Memoize the feedback button (stable JSX)
  const feedbackButton = useMemo(() => (
    <button
      ref={feedbackBtnRef}
      className={`
        w-full h-7 flex items-center rounded
        hover:bg-bg-hover focus:outline-none focus:bg-bg-hover hover:cursor-pointer
        transition-colors duration-200
        ${open ? 'bg-bg-hover text-primary' : ''}
        mb-1
      `}
      onClick={() => setOpen(!open)}
    >
      <span className="flex flex-row items-center px-spacing-2">
        <ScrollIcon className="size-4" aria-hidden="true" />
        <span className="ml-2 whitespace-nowrap text-sm">
          Feedback
        </span>
      </span>
    </button>
  ), [open]);

  // Shared form content (reused in popover and modal)
  const formContent = (
    <motion.div className="w-full h-full flex flex-col justify-between gap-spacing-2">
      <textarea
        ref={feedbackTextareaRef}
        value={localFeedback} // Bind to local state for fast updates
        onChange={onInputChangeEvent}
        placeholder="Feedback"
        className="w-full flex-grow bg-bg-input rounded-md text-primary p-spacing-2 resize-none outline-none"
      />
      
      <div className="w-full flex flex-row justify-between items-center">
        
      <div className="flex flex-row gap-spacing-2 items-center justify-between">
        <div className="flex gap-spacing-2 items-center">
          <button
            type="button"
            aria-label="Attach files to feedback"
            onClick={() => fileInputRef.current?.click()}
            className={`box-border w-8 h-8 rounded-full bg-bg-card flex items-center justify-center border border-border-default hover:bg-bg-hover transition-all duration-200 focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none`}
          >
            <PaperClipIcon className="size-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />
          {!isSmallScreen && (
            <button
              type="button"
              aria-label="Capture screenshot"
              onClick={captureScreenshot}
              className={`box-border w-8 h-8 rounded-full bg-bg-card flex items-center justify-center border border-border-default hover:bg-bg-hover transition-all duration-200 focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none`}
            >
              <CameraIcon className="size-4" />
            </button>
          )}
        </div>

        {(attachments.length > 0 || screenshot) && (
          <div className="flex items-center gap-spacing-2 overflow-x-auto flex-1 min-w-0">
            {attachmentPreviews.map((preview, index) => (
              <AttachmentChip
                key={index}
                type="image"
                name={preview.name}
                url={preview.url}
                onRemove={() => removeAttachment(index)}
                onPreview={() => handleImagePreviewClick({ src: preview.url, alt: preview.name || 'Attached image' })}
                context="feedback"
                variant="pill"
                size="sm"
              />
            ))}
            {screenshotPreview && (
              <AttachmentChip
                type="image"
                name="Screenshot"
                url={screenshotPreview}
                onRemove={removeScreenshot}
                onPreview={() => handleImagePreviewClick({ src: screenshotPreview, alt: 'Screenshot' })}
                context="feedback"
                variant="pill"
                size="sm"
              />
            )}
          </div>
        )}
      </div>

        <SaveBtn
          refProp={popoverRef}
          onClickFn={submitFeedback}
          isDisabled={localFeedback === "" || formState !== "idle"} // Use local for disable check
          idleText={"Send feedback"}
          loadingText={<Spinner size="sm" />}
          successText={"Thank you!"}
        />
      </div>
    </motion.div>
  );

  // Large screens: Popover version
  const feedbackPopover = !isSmallScreen && (
    <AnimatePresence mode="popLayout">
      <motion.div
        ref={popoverRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="fixed h-[256px] w-full max-w-[386px] min-w-[244px] p-spacing-2 border border-border-default rounded-md bg-bg-card shadow-lg z-50"
        style={{
          top: `${popoverPosition.top}px`,
          left: `${popoverPosition.left}px`,
          maxWidth: 'min(386px, calc(100vw - 16px))',
        }}
      >
        {open && formState === "idle" ? formContent : formState === "success" ? (
          <motion.div
            key="success"
            initial={{ y: -32, opacity: 0, filter: "blur(4px)" }}
            animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
            transition={{ type: "spring", duration: 0.4, bounce: 0 }}
            className="w-full h-full flex flex-col items-center justify-center"
          >
            <CheckIcon className="cursor-help size-4 mb-spacing-2" style={{ color: "var(--color-success)" }} />
            <h3>Feedback received!</h3>
            <p>Thanks for helping me improve Consl.</p>
          </motion.div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Spinner size="md" />
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );

  // Small screens: Modal version (centered with backdrop)
  const feedbackModal = isSmallScreen && (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={closeFeedback} // Close on backdrop click
      >
        <motion.div
          ref={modalRef}
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 50, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-[386px] min-w-[244px] p-spacing-2 border border-border-default rounded-md bg-bg-card shadow-lg mx-4"
          onClick={(e) => e.stopPropagation()} // Prevent backdrop close on modal clicks
        >
          {open && formState === "idle" ? (
            <motion.div className="h-[256px] max-w-[386px] min-w-[244px] flex flex-col gap-spacing-2">
              <motion.div className="flex flex-row justify-between px-spacing-1">
                <h2 className="text-dls-h2 font-normal text-primary">Provide feedback</h2>
                <motion.button
                  className="p-spacing-1 rounded hover:bg-bg-hover focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Close"
                  onClick={() => closeFeedback()}
                >
                  <CrossIcon className="size-3" aria-hidden="true" />
                </motion.button>
              </motion.div>
              {formContent}
            </motion.div>

          ) : formState === "success" ? (
            <motion.div
              key="success"
              initial={{ y: -32, opacity: 0, filter: "blur(4px)" }}
              animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
              transition={{ type: "spring", duration: 0.4, bounce: 0 }}
              className="h-[256px] max-w-[386px] min-w-[244px] flex flex-col items-center justify-center"
            >
              <CheckIcon className="cursor-help size-4 mb-spacing-2" style={{ color: "var(--color-success)" }} />
              <h3>Feedback received!</h3>
              <p>Thanks for helping me improve Consl.</p>
            </motion.div>
          ) : (
            <div className="h-[256px] max-w-[386px] min-w-[244px] flex items-center justify-center">
              <Spinner size="md" />
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return (
    <>
      {feedbackButton}
      {open && createPortal(isSmallScreen ? feedbackModal : feedbackPopover, document.body)}
      <ImagePreviewModal
        isOpen={showPreviewModal}
        onClose={handlePreviewModalClose}
        src={selectedPreview?.src}
        alt={selectedPreview?.alt}
      />
    </>
  );
});

FeedbackElement.displayName = 'FeedbackElement';

export default FeedbackElement;
